import {Container, Graphics, LINE_JOIN, Sprite, Text} from "pixi.js";
import {Vector} from "../i.js"; // ../vector.js
import {RES} from "../i.js"; // ../dump.js
import {Thing} from "../i.js"; // ../thing.js
import {Player, PlayerAI} from "../i.js"; // ../player.js
import {UIUtils, Clicker} from "../i.js"; // ../ui/utils.js
import {Tooltip} from "../i.js"; // ../ui/tooltipper.js

class BossBar {
	constructor(dashboard) {
		this.dashboard = dashboard;

		this.display = new Container();
		this._labelBackground = new Graphics();
		this._bossName = new Text("", UIUtils.getFont(0xffffff));
		this._barBackground = new Graphics();
		this._bar = new Graphics();
		this._numbers = new Text("", UIUtils.getFont(0xffffff));
		this._barBorder = new Graphics();
		this._barMask = new Graphics();
		this.display.addChild(this._labelBackground, this._bossName, this._barBackground,
						this._bar, this._numbers, this._barBorder, this._barMask);

		this._barBackground.mask = this._barMask;
		this._bar.mask = this._barMask;
	}

	// entry is an object with at least the following properties:
	//   name
	//   health
	//   maxHealth
	update(dt, position, size, entry) {
		let fraction = entry.health / entry.maxHealth;

		let roundingRadius = size.y / 4;
		let barHeight = Math.round(size.y / 2);
		let barTop = size.y - barHeight;
		let lineWidth = Math.max(Math.round(0.02 * size.y), 1);

		this.display.position.copyFrom(position);

		this._labelBackground
			.clear()
			.beginFill(0x333333, 0.9)
			.moveTo(roundingRadius, 0)
			.arcTo(
				size.x, 0,
				size.x, roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.arcTo(
				0, size.y,
				0, roundingRadius,
				roundingRadius
			)
			.arcTo(
				0, 0,
				roundingRadius, 0,
				roundingRadius
			)
			.closePath();

		this._barMask
			.clear()
			.beginFill(0xffffff)
			.moveTo(roundingRadius, barTop)
			.arcTo(
				size.x, barTop,
				size.x, barTop + roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.arcTo(
				0, size.y,
				0, size.y - roundingRadius,
				roundingRadius
			)
			.arcTo(
				0, barTop,
				roundingRadius, barTop,
				roundingRadius
			)
			.closePath();

		this._barBackground
			.clear()
			.beginFill(0x111111)
			.drawRect(0, barTop, size.x, barHeight);

		this._bar
			.clear()
			.beginFill(0xff4444)
			.drawRect(
				0, barTop,
				lineWidth + fraction * (size.x - 2 * lineWidth), barHeight
			);

		this._barBorder
			.clear()
			.clear()
			.lineStyle({
				width: lineWidth,
				color: 0xffffff,
				join: LINE_JOIN.ROUND,
				alignment: 0
			})
			.moveTo(roundingRadius, barTop)
			.arcTo(
				size.x, barTop,
				size.x, barTop + roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.arcTo(
				0, size.y,
				0, size.y - roundingRadius,
				roundingRadius
			)
			.arcTo(
				0, barTop,
				roundingRadius, barTop,
				roundingRadius
			)
			.closePath();

		this._numbers.text =
			`${Math.floor(entry.health)} / ${Math.floor(entry.maxHealth)}`;
		this._numbers.style.fontSize = 1/3 * size.y;
		this._numbers.position.copyFrom(new Vector(
			(size.x - this._numbers.width) / 2,
			barTop + (barHeight - this._numbers.height) / 2
		).round());

		this._bossName.text = entry.name;
		this._bossName.style.fontSize = 1/3 * size.y;
		this._bossName.position.copyFrom(new Vector(
			(size.x - this._bossName.width) / 2,
			(barTop - this._bossName.height) / 2
		).round());
	}
}



class DashboardBar {
	// colors is an object with the following properties:
	//   barBackground
	//   bar
	//   barBorder
	// tooltip is an object with the following properties:
	//   text
	//   alignment ("LEFT" or "MIDDLE")
	// The instance may store references to these objects, so don't mess with them
	// afterwards!
	constructor(dashboard, getValue, getMaxValue, icon, colors, tooltip) {
		this.dashboard = dashboard;
		this._getValue = getValue;
		this._getMaxValue = getMaxValue;
		this._colors = colors;

		this.display = new Container();
		this._labelBackground = new Graphics();
		this._icon = new Sprite(RES[icon].texture);
		this._icon.anchor.set(0.5, 0.5);
		this._barBackground = new Graphics();
		this._bar = new Graphics();
		this._numbers = new Text("", UIUtils.getFont(0xffffff));
		this._numbers.anchor.set(0, 0.5);
		this._barBorder = new Graphics();
		this._barMask = new Graphics();
		this.display.addChild(this._labelBackground, this._icon, this._barBackground,
						this._bar, this._numbers, this._barBorder, this._barMask);

		this._barBackground.mask = this._barMask;
		this._bar.mask = this._barMask;

		this._tooltip = new Tooltip(
			this.dashboard.game.tooltipper,
			this.display,
			tooltip.text,
			(tooltip.alignment === "LEFT") ? "UP_LEFT" : "UP_MIDDLE"
		);
	}

	update(dt, position, size) {
		let value = this._getValue();
		let maxValue = this._getMaxValue();
		let fraction = value / maxValue;

		let labelWidth = 1.2 * size.y;
		let roundingRadius = size.y / 2;
		let lineWidth = Math.max(Math.round(0.05 * size.y), 1);

		this.display.position.copyFrom(position);

		this._labelBackground
			.clear()
			.beginFill(0x333333, 0.9)
			.moveTo(roundingRadius, 0)
			.lineTo(size.x - roundingRadius, 0)
			.arcTo(
				size.x, 0,
				size.x, roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.lineTo(roundingRadius, size.y)
			.arcTo(
				0, size.y,
				0, roundingRadius,
				roundingRadius
			)
			.arcTo(
				0, 0,
				roundingRadius, 0,
				roundingRadius
			)
			.closePath();

		this._icon.scale.set(0.8 * size.y / this._icon.texture.height);
		this._icon.position.set(labelWidth / 2, size.y / 2);

		this._barMask
			.clear()
			.beginFill(0xffffff)
			.moveTo(labelWidth, 0)
			.lineTo(size.x - roundingRadius, 0)
			.arcTo(
				size.x, 0,
				size.x, roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.lineTo(labelWidth, size.y)
			.closePath();

		this._barBackground
			.clear()
			.beginFill(this._colors.barBackground(fraction))
			.moveTo(labelWidth, 0)
			.lineTo(size.x - roundingRadius, 0)
			.arcTo(
				size.x, 0,
				size.x, roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.lineTo(labelWidth, size.y)
			.closePath();

		this._bar.clear();
		this._bar.beginFill(this._colors.bar);
		this._bar.drawRect(
			labelWidth, 0,
			lineWidth + fraction * (size.x - labelWidth - 2 * lineWidth),
			size.y
		);

		this._barBorder
			.clear()
			.lineStyle({
				width: lineWidth,
				color: (fraction >= 1) ? 0xffffff : this._colors.barBorder,
				join: LINE_JOIN.ROUND,
				alignment: 0
			})
			.moveTo(labelWidth, 0)
			.lineTo(size.x - roundingRadius, 0)
			.arcTo(
				size.x, 0,
				size.x, roundingRadius,
				roundingRadius
			)
			.arcTo(
				size.x, size.y,
				size.x - roundingRadius, size.y,
				roundingRadius
			)
			.lineTo(labelWidth, size.y)
			.closePath();

		this._numbers.text = `${Math.floor(value)} / ${Math.floor(maxValue)}`;
		this._numbers.position.set(1.2 * labelWidth, size.y / 2);
		this._numbers.style.fontSize = 2/3 * size.y;

		this._tooltip.update(dt, position, size);
	}
}



// Source: https://levelup.gitconnected.com/advanced-drawing-with-pixi-js-cd3fddc1d69e
function drawWedge(target,
                   x,
                   y,
                   radius,
                   arc,
                   startAngle = 0,
                   yRadius = 0) {
  let segs = Math.ceil(Math.abs(arc) / 45);
  let segAngle = arc / segs;
  let theta = -(segAngle / 180) * Math.PI;
  let angle = -(startAngle / 180) * Math.PI;
  let ax = x + Math.cos(startAngle / 180 * Math.PI) * radius;
  let ay = y + Math.sin(-startAngle / 180 * Math.PI) * yRadius;
  let angleMid, bx, by, cx, cy;
  if (yRadius === 0)
    yRadius = radius;
  target.moveTo(x, y);
  target.lineTo(ax, ay);
  for (let i = 0; i < segs; ++i) {
    angle += theta;
    angleMid = angle - (theta / 2);
    bx = x + Math.cos(angle) * radius;
    by = y + Math.sin(angle) * yRadius;
    cx = x + Math.cos(angleMid) * (radius / Math.cos(theta / 2));
    cy = y + Math.sin(angleMid) * (yRadius / Math.cos(theta / 2));
    target.quadraticCurveTo(cx, cy, bx, by);
  }
  target.lineTo(x, y);
}



class DashboardButton {
	constructor(dashboard, icon) {
		this.dashboard = dashboard;

		this.display = new Container();
		this._backdrop = new Graphics();
		this._icon = new Sprite(RES[icon].texture);
		this._icon.anchor.set(0.5, 0.5);
		this._cooldown = new Graphics();
		this._cooldownMask = new Graphics();
		this.display.addChild(this._backdrop, this._icon, this._cooldown,
						this._cooldownMask);

		this._cooldown.mask = this._cooldownMask;

		this._clicker = new Clicker(this.display);
		this._tooltip = new Tooltip(
			this.dashboard.game.tooltipper,
			this.display,
			"Nanobots (E)"
							+ "\n\nRepair you, or whatever else they hit, for a total of"
							+ " 10 b."
							+ "\n\nCost:"
							+ "\n70 energy"
							+ "\n5 seconds of Vulnerable"
							+ "\n\nCooldown: 5 seconds",
			"UP_MIDDLE"
		);
	}

	update(dt, position, size) {
		let cooldownFraction = this.dashboard.game.space.player.ai.healingCooldown
						/ PlayerAI.HEALING_COOLDOWN;
		let roundingRadius = size.y / 4;
		let lineWidth = Math.max(Math.round(0.05 * size.y), 1);

		this.display.position.copyFrom(position);

		this._backdrop
			.clear()
			.lineStyle({
				width: lineWidth,
				color: (cooldownFraction > 0) ? 0x888888
					: (this._clicker.isHovering() ? 0xffffff : 0x21e521),
				join: LINE_JOIN.ROUND,
				alignment: 0
			})
			.beginFill((cooldownFraction > 0) ? 0x202020
				: (this._clicker.isHovering() ? 0x005000 : 0x004000))
			.drawRoundedRect(0, 0, size.x, size.y, roundingRadius);

		this._icon.scale.set(0.7 * size.y / this._icon.texture.height);
		this._icon.position.set(size.x / 2, size.y / 2);
		this._icon.tint = (cooldownFraction > 0) ? 0xbbbbbb : 0x21e521;

		this._cooldown.clear();
		if (cooldownFraction > 0) {
			this._cooldown.beginFill(0xaaaaaa, 0.7);
			drawWedge(
				this._cooldown,
				size.x / 2, size.y / 2,
				size.x,
				-360 * cooldownFraction,
				90,
				size.y
			);
		}

		this._cooldownMask
			.clear()
			.beginFill(0xffffff)
			.drawRoundedRect(0, 0, size.x, size.y, roundingRadius);

		this._tooltip.update(dt, position, size);

		this._clicker.clearStuff();
	}

	wasClicked() {
		return this._clicker.wasClicked();
	}
}



class DashboardIndicator {
	constructor(dashboard, icon) {
		this.dashboard = dashboard;

		this.display = new Container();
		this._icon = new Sprite(RES[icon].texture);
		this.display.addChild(this._icon);

		this._tooltip = new Tooltip(
			this.dashboard.game.tooltipper,
			this.display,
			"Vulnerable"
							+ "\nStatus effect"
							+ "\n\nMakes you take 3x damage from all sources.",
			"UP_MIDDLE"
		);
	}

	update(dt, position, size) {
		let visible = this.dashboard.game.space.player.vulnerable;

		this._tooltip.update(dt, position, size);

		if (!visible) {
			this.display.visible = false;
			return;
		}

		this.display.visible = true;
		this.display.position.copyFrom(position);
		this._icon.scale.set(size.y / this._icon.texture.height);
	}
}



export class Dashboard {
	// Create a new dashboard
	// Only create one dashboard per game. game is a reference to the game.
	constructor(game) {
		this.game = game;

		this.display = new Container();

		this._bossBars = [];

		this.healthBar = new DashboardBar(
			this,
			() => this.game.space.player.health,
			() => this.game.space.player.maxHealth,
			"images/icons/health.svg",
			{
				barBackground: (fraction) => (fraction > 0.5)
					? 0x004000
					: ((fraction > 0.25) ? 0x404000 : 0x400000),
				bar: 0x00e600,
				barBorder: 0xb2ffb2
			},
			{
				text: "Health"
								+ "\n\nGet this number to 0 to lose all your items and"
								+ " respawn at the last visited station :)",
				alignment: "LEFT"
			}
		);
		this.display.addChild(this.healthBar.display);

		this.energyBar = new DashboardBar(
			this,
			() => this.game.space.player.energy,
			() => Player.MAX_ENERGY,
			"images/icons/energy.svg",
			{
				barBackground: (fraction) => 0x2d004d, // hue 275
				bar: 0xb654ff,
				barBorder: 0xe4bfff
			},
			{
				text: "Energy"
								+ "\n\nUsed for all sorts of actions."
								+ " Regenerates over time, but only while you don't use"
								+ " it.",
				alignment: "MIDDLE"
			}
		);
		this.display.addChild(this.energyBar.display);

		this.healingButton = new DashboardButton(
			this,
			"images/icons/healing.svg"
		);
		this.display.addChild(this.healingButton.display);

		this.vulnerableIndicator = new DashboardIndicator(
			this,
			"images/icons/vulnerable.svg"
		);
		this.display.addChild(this.vulnerableIndicator.display);
	}

	update(dt, screenSize) {
		let rulerLength = UIUtils.getRulerLength(screenSize);
		let dashboardOffset = Math.round((screenSize.x - rulerLength) / 2);

		this._updateTop(dt, screenSize, rulerLength);
		this._updateBottom(dt, screenSize, rulerLength, dashboardOffset);
	}

	// Update top part of the dashboard
	_updateTop(dt, screenSize, dashboardWidth) {
		let marginWidth = Math.round(0.02 * dashboardWidth);

		// TODO: Make sure this is sorted by ID
		// Not using tactical report here to avoid 1-frame lag and avoid cluttering the
		// tactical report with showBossBar / health / maxHealth.
		let playerPos = this.game.space.player.pos;
		let bossBarData = this.game.space.things
			.filter(
				thing =>
					(thing.state === Thing.STATES.ALIVE)
					&& thing.showBossBar
					&& (Vector.diff(thing.pos, playerPos).mag() < 10)
				)
			.map(thing => ({
				id: thing.id,
				name: thing.name,
				health: thing.health,
				maxHealth: thing.maxHealth
			}))
			// Note: This will break if ids ever become non-numeric
			.sort((a, b) => a.id - b.id);
		let numBars = bossBarData.length;

		let singleBarWidth = Math.round(Math.min(
			0.5 * dashboardWidth,
			(0.75 * dashboardWidth + marginWidth) / numBars - marginWidth
		));
		let barSize = new Vector(singleBarWidth, 0.05 * dashboardWidth).round();

		let totalWidth = numBars * (singleBarWidth + marginWidth) - marginWidth;
		let basePosition = new Vector(
			(screenSize.x - totalWidth) / 2,
			marginWidth
		).round();

		UIUtils.cullElements(this._bossBars, numBars);
		for (let i = 0; i < numBars; ++i) {
			if (this._bossBars.length <= i) {
				let bossBar = new BossBar(this);
				this._bossBars.push(bossBar);
				this.display.addChild(bossBar.display);
			}

			let bossBar = this._bossBars[i];
			let barPosition = Vector.sum(basePosition, new Vector(
				i * (singleBarWidth + marginWidth), 0
			));
			bossBar.update(dt, barPosition, barSize, bossBarData[i]);
		}
	}

	// Update bottom part of the dashboard
	_updateBottom(dt, screenSize, dashboardWidth, dashboardOffset) {
		let healthBarSize = new Vector(
			0.2 * dashboardWidth,
			0.035 * dashboardWidth
		).round();
		let healthBarPosition = new Vector(
			dashboardOffset + 0.02 * dashboardWidth,
			screenSize.y - 0.02 * dashboardWidth - healthBarSize.y
		).round();
		this.healthBar.update(dt, healthBarPosition, healthBarSize);

		let energyBarSize = new Vector(healthBarSize);
		let energyBarPosition = new Vector(
			healthBarPosition.x + healthBarSize.x + 0.02 * dashboardWidth,
			healthBarPosition.y
		).round();
		this.energyBar.update(dt, energyBarPosition, energyBarSize);

		let healingButtonSize = new Vector(
			0.035 * dashboardWidth,
			0.035 * dashboardWidth
		).round();
		let healingButtonPosition = new Vector(
			energyBarPosition.x + energyBarSize.x + 0.02 * dashboardWidth,
			healthBarPosition.y
		).round();
		this.healingButton.update(dt, healingButtonPosition, healingButtonSize);

		let vulnerableIndicatorSize = new Vector(
			0.035 * dashboardWidth,
			0.035 * dashboardWidth
		).round();
		let vulnerableIndicatorPosition = new Vector(
			healingButtonPosition.x + healingButtonSize.x + 0.02 * dashboardWidth,
			healthBarPosition.y
		).round();
		this.vulnerableIndicator.update(dt, vulnerableIndicatorPosition,
						vulnerableIndicatorSize);
	}
}
